import * as Sentry from '@sentry/vue'

function parseTracesSampleRate(raw, fallback) {
  if (raw === undefined || raw === '') return fallback
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 0) return fallback
  return Math.min(1, n)
}

/**
 * Client-side error and (optional) performance reporting. No-op when VITE_SENTRY_DSN is unset.
 * Call after createApp(App), before app.mount(), and pass the same router instance used by the app.
 *
 * Browser performance tracing is **opt-in** (VITE_SENTRY_TRACES_SAMPLE_RATE > 0). When enabled, Sentry
 * POSTs envelopes on navigation; ad blockers often block ingest.de.sentry.io (net::ERR_BLOCKED_BY_CLIENT),
 * which spams the console and feels like “pages are broken” even though routing still works. Errors
 * still POST when tracing is off; set the env var when you want performance traces and can accept that.
 */
export function initSentry(app, router) {
  const dsn = (import.meta.env.VITE_SENTRY_DSN || '').trim()
  if (!dsn) return

  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development').trim() ||
    'development'
  const release = (import.meta.env.VITE_SENTRY_RELEASE || '').trim() || undefined
  const tracesSampleRate = parseTracesSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0)

  const integrations = []
  if (tracesSampleRate > 0 && router) {
    integrations.push(
      Sentry.browserTracingIntegration({
        router,
        instrumentNavigation: true,
        instrumentPageLoad: true,
      }),
    )
  }

  Sentry.init({
    app,
    dsn,
    environment,
    release,
    sendDefaultPii: true,
    integrations,
    tracesSampleRate,
    // Post-deploy stale tabs: old shell requests removed hashed chunks; we reload once via chunkLoadRecovery.
    ignoreErrors: [
      'Failed to fetch dynamically imported module',
      'error loading dynamically imported module',
      'Importing a module script failed',
      /^Loading chunk [\w-]+ failed$/i,
    ],
  })
}

/**
 * Attach the signed-in account to Sentry error events (user panel in the issue).
 * Uses public uuid as `id` (never internal DB id). No-op when Sentry DSN is unset.
 * Call with `null` on logout — exported for tests or rare flows; the app store watches `user` and syncs automatically.
 */
export function syncSentryUser(userLike) {
  const dsn = (import.meta.env.VITE_SENTRY_DSN || '').trim()
  if (!dsn) return

  if (!userLike) {
    Sentry.setUser(null)
    return
  }

  const id = userLike.uuid ?? userLike.id
  const email = userLike.email
  if (!id && !email) {
    Sentry.setUser(null)
    return
  }

  Sentry.setUser({
    ...(id != null && id !== '' ? { id: String(id) } : {}),
    ...(email ? { email } : {}),
    ...(userLike.name ? { username: userLike.name } : {}),
  })
}

export function captureSentryException(error, captureContext) {
  if (!error) return
  Sentry.captureException(error, captureContext)
}
